<template>
  <div class="flex flex-wrap justify-center divide-x p-4">
    <div class="w-full lg:w-2/3">
      <div class="mb-4 flex items-center gap-4">
        <h3>{{ t('filtration') }}</h3>
        <div class="flex items-center gap-1 text-sm">
          <icon name="ic_system_info" class="h-5 w-5 text-cdp-main-primary" />
          {{ t('up_10_filter_conditions_select_quantity') }} ：
          {{ filtration.pool.length }}
        </div>
      </div>
      <div class="h-[50vh] overflow-y-auto">
        <div
          v-for="(pool, poolIdx) in filtration.pool"
          :key="`pool-${poolIdx}`"
          class="mb-4 mr-4 rounded-md bg-cdp-grey-100 p-4 shadow-md"
        >
          <div class="flex justify-between">
            <h3 class="text-sm">
              {{ pool.name }}
            </h3>
            <button
              class="disabled-text"
              :disabled="disabled"
              @click="deleteFiltration(poolIdx)"
            >
              <icon name="ic_common_delete" class="h-6 w-6" />
            </button>
          </div>
          <div>
            <div class="flex flex-wrap items-center justify-start gap-2 p-4">
              <div
                v-for="(option, optionIdx) in pool.options"
                :key="`option-${optionIdx}`"
                class="fllex w-full items-center justify-start py-2"
              >
                <component
                  :is="option.option?.editor"
                  :id="`${pool.key}-${pool.conditionId}-${option.id}`"
                  v-model="componentsProps.defaultModellMap[pool.key][option?.id]"
                  v-bind="
                    componentsProps.defaultSetMap[pool.key][option?.option.editor][
                      option.id
                    ]
                  "
                  class="w-auto"
                  :disabled="props.disabled"
                  :append-to-body="true"
                />
              </div>
            </div>
            <hr class="my-3" />
            <div class="flex items-center">
              <checkbox
                :id="`ex-clude-${pool.key}-${poolIdx}`"
                v-model="filtration.exclude[pool.key]"
                :text="t('exclude_this_audience')"
                :disabled="props.disabled"
              />
            </div>
          </div>
        </div>
        <div
          v-show="!filtration.pool.length"
          class="flex h-full items-center justify-center"
        >
          <div class="flex w-full flex-wrap justify-center">
            <icon name="ic_common_filter_empty" class="h-40 w-40 text-gray-300" />
            <div class="w-full">
              <h3 class="text-center text-sm">
                {{ t('plz_filter_criteria_from_right') }}
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4 flex justify-center gap-2">
        <slot name="footer" />
      </div>
    </div>
    <div class="w-full px-4 lg:w-1/3">
      <h3 class="mb-4">{{ t('filter_condition') }}</h3>
      <nav-tab v-model="props.condition.tab" :items="props.condition.tabItems" dense />
      <div v-if="props.condition.pool" class="h-[50vh] divide-y overflow-y-auto">
        <div
          v-for="(pool, poolIdx) in props.condition.pool[props.condition.tab]"
          :key="`pool-${poolIdx}`"
          class="p-3"
        >
          <div class="flex justify-between text-sm">
            <span>
              {{ pool.name }}
            </span>
            <button
              class="disabled-text text-cdp-yellow-400 duration-300 hover:text-cdp-yellow-300"
              :disabled="disabled || filtration.pool.length === 10"
              @click="addFiltration(pool)"
            >
              <icon name="ic_common_create" class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { formatChartConfig } from '@/composables/formatChartConfig.js';

import lo_ from '@/plugins/lodash.js';

import navTab from '@/components/NavTab.vue';

import { reactive, watch, defineComponent } from 'vue';

export default defineComponent({
  name: 'ComponentsSelectFilter',
  components: {
    navTab
  },
  props: {
    condition: {
      type: Object,
      default: () => {
        return {
          tab: '',
          tabItems: [],
          poolAll: [],
          pool: []
        };
      }
    },
    data: {
      type: Object,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const filtration = reactive({
      pool: [],
      exclude: {}
    });

    const componentsProps = reactive({
      defaultModellMap: {
        // chartId + editor = props
      },
      defaultSetMap: {
        // chartId + editor + editorId = props
      }
    });

    watch(
      () => props.data,
      () => {
        revertFiltration();
      }
    );

    watch(
      () => [componentsProps.defaultModellMap, filtration.exclude],
      () => {
        formatSend();
      },
      { deep: true, immediate: false }
    );

    function revertFiltration() {
      if (props.data) {
        filtration.pool = [];
        filtration.exclude = {};
        props.data.forEach((item, idx) => {
          props.condition.poolAll.forEach((pool) => {
            // set key for filtration
            const key = item.conditionId + idx;

            const condition = lo_.find(pool, { conditionId: item.conditionId });
            if (condition) {
              addFiltration(condition);

              // set exclude default val
              filtration.exclude[key] = item.exclude;

              condition.options.forEach((option) => {
                // set data default val
                componentsProps.defaultModellMap[key][option.id] =
                  item.data[lo_.camelCase(option.id)];
              });
            }
          });
        });
      }
    }

    function addFiltration(pool) {
      if (filtration.pool.length > 9) return;
      // set key for filtration
      const key = pool.conditionId + filtration.pool.length;

      const obj = {
        key,
        ...pool
      };

      pool.options.forEach((item) => {
        item.option.editor = lo_.camelCase(item.option.editor);

        const { defaultVal, props } = formatChartConfig(item.option);

        if (!componentsProps.defaultModellMap[key]) {
          componentsProps.defaultModellMap[key] = {};
        }

        if (!componentsProps.defaultSetMap[key]) {
          componentsProps.defaultSetMap[key] = {};
        }

        // components v-model
        componentsProps.defaultModellMap[key][item.id] = defaultVal;

        // structure components props
        componentsProps.defaultSetMap[key][item.option.editor] = Object.assign(
          {},
          componentsProps.defaultSetMap[key][item.option.editor]
        );
        // components props
        componentsProps.defaultSetMap[key][item.option.editor][item.id] = props;
      });

      filtration.exclude[key] = false;
      filtration.pool.push(obj);
    }

    function deleteFiltration(poolIdx) {
      filtration.pool = filtration.pool.filter((item, idx) => idx !== poolIdx);
      formatSend();
    }

    function formatSend() {
      const result = [];
      filtration.pool.forEach((item) => {
        const form = {
          data: {},
          exclude: filtration.exclude[item.key],
          conditionId: item.conditionId
        };

        item.options.forEach((option) => {
          form.data[option.id] = componentsProps.defaultModellMap[item.key][option.id];
        });

        result.push(form);
      });

      emit('change', result);
    }

    return {
      t,
      props,
      filtration,
      componentsProps,
      addFiltration,
      deleteFiltration
    };
  }
});
</script>